import * as React from "react";
import { Button, Stack, Typography } from "@mui/material";
import {
  CategoryProductDialog,
  CustomTable,
  CustomeDialog,
  DeleteModal,
} from "../../../shared";
import { sortItems } from "../../../utils";
import AddIcon from "@mui/icons-material/Add";
import InventoryIcon from '@mui/icons-material/Inventory';
import { useParams } from "react-router-dom";
import { usePointPromotionProducts } from "../hooks";

export const PointPromotionProducts = ({ data }) => {
  const { id } = useParams();
  const {
    state,
    openAssignModal,
    closeAssignModal,
    HandleFilterProduct,
    handleProductSelect,
    handleAssignProduct,
    handleSelectAllProducts,
    DeleteModals,
    handleDeleteProduct,
    applyToAllProducts,
    handleApplyAllConfirmModal,
  } = usePointPromotionProducts({
    promotionId: id,
    promotionData: data,
  });

  const ActionButtons = ({ row, deleteAction }) => {
    return (
      <div className="action-buttons" onClick={(e) => e.preventDefault()}>
        <img
          alt="delete"
          onClick={deleteAction}
          src="/icons/ic_delete.svg"
          style={{
            cursor: "pointer",
            opacity: 1,
          }}
        />
      </div>
    );
  };

  const columns = React.useMemo(
    () => [
      {
        Header: "Product Name",
        accessor: "name",
        sortType: (prev, curr, columnId) => {
          return sortItems(prev, curr, columnId);
        },
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        disableGlobalFilter: true,
        accessor: (row) => {
          return (
            <ActionButtons
              row={row}
              deleteAction={() => {
                DeleteModals(true, row.id);
              }}
            />
          );
        },
      },
    ],
    [],
  );

  const selectedProduct = state.assign.productList?.find(
    (item) => item.id === state.productId,
  );
  const isLastSelectedProduct =
    (state.assign.productList?.length || 0) === 1 && !!selectedProduct;
  const deleteModalDescription = isLastSelectedProduct ? (
    <>
      This is the last selected product. After deleting, this will no longer be
      limited to specific products and will apply to all products.
      <br />
      <br />
      {`Are you sure you want to Delete the Product "${selectedProduct?.name}" ?`}
    </>
  ) : (
    "Are you sure you want to remove this product from the promotion?"
  );

  return (
    <>
      {state.assign.productList?.length ? (
        <CustomTable
          columns={columns}
          data={state.assign.productList || []}
          isLoading={state.isBusy}
          searchBar={true}
          defaultSearch={true}
          tableTitle="Products"
          bulkDelete={false}
          emptyMessage="No products found"
          headerRightContent={
            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                size="medium"
                variant="text"
                onClick={() => handleApplyAllConfirmModal(true)}
              >
                Apply To All Products
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAssignModal}
              >
                Add/Modify Products
              </Button>
            </Stack>
          }
        />
      ) : (
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            gap: "16px",
            border: "2px Dashed #D1D5DC",
            borderRadius: "12px",
            padding: "16px",
            my: "32px",
          }}
        >
          <Stack
            sx={{
              borderRadius: "50px",
              minWidth: "40px",
              height: "40px",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#E0E7FF",
            }}
          >
            <InventoryIcon color="primary" />
          </Stack>
          <Stack sx={{ width: "100%", gap: "4px" }}>
            <Typography
              fontWeight="bold"
              fontSize="18px"
              lineHeight="20px"
              color="#111111"
            >
              Available to all Products
            </Typography>
            <Typography fontWeight={400} fontSize="14px" color="#636363">
              This promotion is available to all products from Customer Card.
              You can restrict access by selecting specific products.
            </Typography>
          </Stack>
          <Button
            type="button"
            size="medium"
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ minWidth: "220px" }}
            onClick={openAssignModal}
          >
            Add Products
          </Button>
        </Stack>
      )}

      <DeleteModal
        open={state.isDelete}
        handleClose={() => DeleteModals(false)}
        onConfirm={handleDeleteProduct}
        isDeleteButton={state.isBusy}
        title="Delete Product"
        description={deleteModalDescription}
      />

      <CustomeDialog
        open={state.isApplyAllConfirmOpen}
        PaperProps={{
          sx: { width: "423px", p: "16px" },
        }}
        title="Apply To All Products"
        content={
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This will change access from specific products to all products. Do
              you want to continue?
            </Typography>
            <Stack direction="row" justifyContent="flex-end" gap={2} mt={2}>
              <Button
                type="button"
                size="medium"
                onClick={() => handleApplyAllConfirmModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="medium"
                variant="contained"
                onClick={applyToAllProducts}
                disabled={state.isBusy}
              >
                Confirm
              </Button>
            </Stack>
          </Stack>
        }
      />

      <CustomeDialog
        open={state.isOpen}
        handleClose={closeAssignModal}
        aria-labelledby="point-promotion-products-dialog"
        aria-describedby="point-promotion-products-dialog-content"
        PaperProps={{
          sx: { width: "423px", height: "535px", p: "16px" },
        }}
        title="Products"
        content={
          <CategoryProductDialog
            Items={state.assign.filteredProducts}
            onSearch={HandleFilterProduct}
            onSelect={handleProductSelect}
            selectedItems={state.assign.selectedProducts}
            existedProductList={state.assign.productList.map((p) => p.id)}
            onSubmit={handleAssignProduct}
            disabled={state.assign.isSaveButtonBusy}
            type="modify"
            isLoading={state.assign.isModalLoading}
            isButtonLoading={state.assign.isSaveButtonBusy}
            onSelectAll={handleSelectAllProducts}
            hasVariantLevel={true}
          />
        }
      />
    </>
  );
};
